/**
 * v3.19.0 — Extraction-status pill for Documents-First flow.
 *
 * Renders nothing when status is null/undefined/"completed" (the
 * record is in its normal state — no badge needed). Renders an
 * amber "Extracting…" with a spinner while pending/running, and a
 * red "Extraction failed — edit manually" pill when failed.
 *
 * Props:
 *   - status: "pending" | "running" | "completed" | "failed" | null
 *   - error: optional error string (shown as a title tooltip)
 */
import { Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function ExtractionStatusBadge({ status, error, className }) {
  if (!status || status === 'completed') return null;

  if (status === 'pending' || status === 'running') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5',
          'bg-amber-100 text-amber-700 border border-amber-200',
          className,
        )}
        data-testid="extraction-status-pending"
        title="AI is extracting metadata from the uploaded file…"
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Extracting…
      </span>
    );
  }

  if (status === 'failed') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5',
          'bg-rose-100 text-rose-700 border border-rose-200',
          className,
        )}
        data-testid="extraction-status-failed"
        title={error || 'Extraction failed — edit fields manually.'}
      >
        <AlertTriangle className="w-3 h-3" />
        Edit manually
      </span>
    );
  }

  return null;
}
